import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Minus, Plus, Loader2, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ASSETS } from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingForm() {
  const [dates, setDates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [over18, setOver18] = useState(false);
  const [allergies, setAllergies] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/dates`).then((r) => {
      const ds = r.data.dates || [];
      setDates(ds);
      const firstAvailable = ds.find((d) => !d.sold_out) || ds[0];
      if (firstAvailable) setSelectedId(firstAvailable.id);
    }).catch(() => {});
  }, []);

  const selected = dates.find((d) => d.id === selectedId);
  const soldOut = selected?.sold_out || dates.length === 0;
  const remaining = selected?.remaining ?? 0;
  const capacity = selected?.capacity ?? 0;
  const sold = selected?.sold ?? 0;
  const pricePence = selected?.price_pence ?? 1250;
  const total = ((quantity * pricePence) / 100).toFixed(2);

  const changeQty = (d) => setQuantity((q) => Math.min(Math.max(1, q + d), Math.max(remaining, 1)));

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedId) {
      toast.error("Please choose a date.");
      return;
    }
    if (!name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!over18) {
      toast.error("Please confirm you are 18 or over.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings/checkout`, {
        name,
        email,
        quantity,
        over18,
        allergies,
        date_id: selectedId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="book" className="px-6 sm:px-10 lg:px-16 py-28 lg:py-36 relative">
      <div className="absolute inset-0 spotlight pointer-events-none" aria-hidden="true" />
      <div className="mx-auto max-w-xl relative z-10">
        <Reveal>
          <div className="ornate-frame bg-[var(--surface)] p-8 sm:p-12">
            <div className="flex flex-col items-center text-center mb-8">
              <img src={ASSETS.crest} alt="" className="w-16 mb-4 rounded-full" />
              <p className="uppercase tracking-[0.35em] text-[11px] text-[var(--gold)]">Advance Tickets</p>
              <h2 className="font-display text-4xl sm:text-5xl text-[var(--cream)] mt-3">Reserve your seat</h2>
              <p className="font-serif-alt text-lg text-[var(--muted)] mt-3" data-testid="tickets-status">
                {dates.length === 0
                  ? "Ticket sales are currently closed. Check back soon."
                  : soldOut ? "This event is now sold out." : `${remaining} of ${capacity} places remaining`}
              </p>
              {!soldOut && dates.length > 0 && (
                <div data-testid="tickets-sold-count" className="mt-4 inline-flex items-center gap-2 border border-[rgba(201,162,74,0.35)] px-4 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />
                  <span className="font-body uppercase tracking-[0.2em] text-[11px] text-[var(--cream)]">
                    <span className="text-[var(--gold)] font-semibold">{sold}</span> of {capacity} tickets sold
                  </span>
                </div>
              )}
            </div>

            <form onSubmit={submit} className="space-y-7">
              {dates.length > 0 && (
                <div>
                  <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Choose your date</label>
                  <div className="space-y-3" data-testid="date-selector">
                    {dates.map((d) => {
                      const active = d.id === selectedId;
                      return (
                        <button
                          type="button"
                          key={d.id}
                          data-testid={`date-option-${d.id}`}
                          onClick={() => { setSelectedId(d.id); setQuantity(1); }}
                          disabled={d.sold_out}
                          className={`w-full flex items-center justify-between px-5 py-4 border text-left transition-colors duration-200 ${active ? "border-[var(--gold)] bg-[rgba(201,162,74,0.1)]" : "border-[rgba(201,162,74,0.3)] hover:border-[var(--gold)]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          <span className="pr-4">
                            {d.title && <span className="font-body text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] block mb-1">{d.title}</span>}
                            <span className="font-serif-alt text-xl text-[var(--cream)] block">{d.label}</span>
                            <span className="font-body text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
                              {d.time}{d.location ? ` · ${d.location}` : ""}
                            </span>
                            {d.description && <span className="font-body text-xs text-[var(--muted)] block mt-1.5 normal-case tracking-normal leading-relaxed">{d.description}</span>}
                          </span>
                          <span className="font-body text-[11px] uppercase tracking-[0.15em] text-right">
                            <span className="text-[var(--cream)] block">£{(((d.price_pence ?? 1250)) / 100).toFixed(2)}</span>
                            {d.sold_out ? <span className="text-[var(--muted)]">Sold out</span> : <span className="text-[var(--gold)]">{d.remaining} left</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Your name</label>
                <input
                  data-testid="booking-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  className="field-input"
                  disabled={soldOut}
                  required
                />
              </div>

              <div>
                <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Email for your ticket</label>
                <input
                  data-testid="booking-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="field-input"
                  disabled={soldOut}
                  required
                />
              </div>

              <div>
                <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Number of tickets</label>
                <div className="flex items-center justify-between border border-[rgba(201,162,74,0.35)] px-4 py-2">
                  <button
                    type="button"
                    data-testid="booking-qty-minus"
                    onClick={() => changeQty(-1)}
                    disabled={soldOut || quantity <= 1}
                    className="w-10 h-10 flex items-center justify-center text-[var(--gold)] hover:bg-[rgba(201,162,74,0.12)] disabled:opacity-30 transition-colors duration-200"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span data-testid="booking-qty-value" className="font-display text-3xl text-[var(--cream)]">{quantity}</span>
                  <button
                    type="button"
                    data-testid="booking-qty-plus"
                    onClick={() => changeQty(1)}
                    disabled={soldOut || quantity >= remaining}
                    className="w-10 h-10 flex items-center justify-center text-[var(--gold)] hover:bg-[rgba(201,162,74,0.12)] disabled:opacity-30 transition-colors duration-200"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Any allergies? (optional)</label>
                <textarea
                  data-testid="booking-allergies-input"
                  value={allergies}
                  onChange={(e) => setAllergies(e.target.value)}
                  placeholder="Let us know about any allergies or dietary needs"
                  rows={2}
                  className="field-input resize-none"
                  disabled={soldOut}
                />
              </div>

              <button
                type="button"
                data-testid="booking-over18-checkbox"
                onClick={() => setOver18((v) => !v)}
                disabled={soldOut}
                className="flex items-center gap-3 w-full text-left group"
              >
                <span className={`w-6 h-6 border flex items-center justify-center transition-colors duration-200 ${over18 ? "bg-[var(--gold)] border-[var(--gold)]" : "border-[rgba(201,162,74,0.5)]"}`}>
                  {over18 && <Check className="w-4 h-4 text-[var(--bg)]" />}
                </span>
                <span className="font-body text-sm text-[var(--cream)]">I confirm I am 18 years of age or over.</span>
              </button>

              <div className="flex items-center justify-between pt-4 border-t border-[rgba(201,162,74,0.18)]">
                <span className="font-body uppercase tracking-[0.2em] text-[11px] text-[var(--muted)]">Total</span>
                <span data-testid="booking-total" className="font-display text-3xl text-[var(--gold)]">£{total}</span>
              </div>

              <button
                type="submit"
                data-testid="booking-submit-btn"
                disabled={loading || soldOut}
                className="w-full rounded-full bg-[var(--gold)] text-[var(--bg)] py-4 uppercase tracking-[0.2em] text-xs font-semibold flex items-center justify-center gap-2 transition-colors duration-300 hover:bg-[var(--cream)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                ) : soldOut ? (
                  dates.length === 0 ? "Sales closed" : "Sold out"
                ) : (
                  "Pay with card"
                )}
              </button>
              <p className="text-center font-body text-[11px] text-[var(--muted)]">
                Secure payment by Stripe · You'll receive your ticket by email.
              </p>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
